import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Calculator } from "lucide-react";
import DateInput from "@/components/DateInput";
import { fmtTL, fmtNum, todayISO } from "@/lib/utils";
import { P_FIELDS, plabel } from "@/lib/contracts";
import { PaymentReport } from "@/components/contracts/PaymentReport";
import { Warnings } from "@/components/contracts/Warnings";
import { useApprovers } from "@/components/contracts/Approvers";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const num = (v) => Number(v) || 0;

function buildInitial(contract, payment) {
  const ps = contract.payments;
  const prev = payment ? ps.filter(p => p.no < payment.no) : ps;
  const prevLast = [...prev].reverse().find(p => p.lines?.length);
  const prevQty = Object.fromEntries((prevLast?.lines || []).map(l => [l.item_id, l.cum_qty]));
  const src = payment || { no: (ps.at(-1)?.no || 0) + 1, label: "", date: todayISO(), period: "", extra_cum: ps.at(-1)?.calc?.B || 0,
    sgk: 0, other_ded: 0, advance_override: null, p: {}, status: "hazirlandi", notes: "", lines: [] };
  const cur = Object.fromEntries((src.lines || []).map(l => [l.item_id, l.cum_qty]));
  return { form: { ...src, advance_override: src.advance_override ?? "", qty: Object.fromEntries(contract.items.map(i => [i.id, cur[i.id] ?? prevQty[i.id] ?? 0])) }, prevQty };
}

export function PaymentForm({ open, onOpenChange, contract, payment, onSaved }) {
  const init = useMemo(() => buildInitial(contract, payment), [contract, payment]);
  const [f, setF] = useState(init.form);
  const [calc, setCalc] = useState(null);
  const [saving, setSaving] = useState(false);
  const { list: approvers } = useApprovers(open);
  const preparers = approvers.filter(a => a.roles.includes("hazirlandi"));
  useEffect(() => { if (open) { setF(init.form); setCalc(null); } }, [open, init]);

  const body = () => ({ no: num(f.no), label: f.label, date: f.date, period: f.period, status: f.status, notes: f.notes,
    lines: payment?.fixed ? payment.lines : contract.items.map(i => ({ item_id: i.id, cum_qty: num(f.qty[i.id]) })), extra_cum: num(f.extra_cum), sgk: num(f.sgk),
    other_ded: num(f.other_ded), advance_override: f.advance_override === "" ? null : num(f.advance_override),
    p: Object.fromEntries(P_FIELDS.map(([k]) => [k, num(f.p?.[k])])), deduction_ids: f.deduction_ids || [], prepared_by: payment ? null : (f.prepared_by || null) });

  useEffect(() => {
    if (!open || !f.date) return;
    const t = setTimeout(() => api.post(`/contracts/${contract.id}/preview${payment ? `?payment_id=${payment.id}` : ""}`, body())
      .then(r => setCalc(r.data)).catch(() => {}), 350);
    return () => clearTimeout(t);
  }, [f, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const pullDeductions = async () => {
    try {
      const r = await api.get(`/contracts/${contract.id}/deduction-suggest${payment ? `?payment_id=${payment.id}` : ""}`);
      const p = Object.fromEntries(P_FIELDS.map(([k]) => [k, r.data.p[k] || 0]));
      setF({ ...f, sgk: r.data.sgk || f.sgk, p: { ...p, p2: f.p?.p2 || 0, p3: f.p?.p3 || 0 }, deduction_ids: r.data.ids });
      toast.success(`${r.data.count} kesinti aktarıldı; kaydedince "uygulandı" olarak işaretlenir`);
    } catch (e) { toast.error(e.response?.data?.detail || "Kesintiler getirilemedi"); }
  };
  const sgkHelper = () => {
    const c = calc?.calc?.C || 0, paid = num(f.sgk_declared);
    const diff = c * contract.rates.sgk_rate - paid;
    setF({ ...f, sgk: Math.max(diff * contract.rates.sgk_prim, 0).toFixed(2) });
  };
  const save = async () => {
    if (!f.date) { toast.error("Hakediş tarihi zorunlu"); return; }
    setSaving(true);
    try {
      payment ? await api.put(`/contract-payments/${payment.id}`, body()) : await api.post(`/contracts/${contract.id}/payments`, body());
      toast.success("Hakediş kaydedildi"); onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); } finally { setSaving(false); }
  };
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="payment-form">
        <DialogHeader><DialogTitle>{payment ? `${plabel(payment)} Hakedişi Düzenle` : "Yeni Hakediş"} — {contract.firm_name}</DialogTitle></DialogHeader>
        {payment?.fixed && <div className="text-xs rounded-md bg-sky-500/10 border border-sky-500/30 p-2">Bu hakediş Excel'den aktarıldı; tutarlar Excel'deki değerlerle sabittir.</div>}
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
          <div className="space-y-4">
            <div className="grid grid-cols-4 gap-3">
              <div><Label>No</Label><Input type="number" value={f.no} onChange={set("no")} data-testid="pf-no" /></div>
              <div><Label>Etiket</Label><Input value={f.label} onChange={set("label")} placeholder="7 NOLU" /></div>
              <div><Label>Tarih *</Label><DateInput value={f.date} onChange={set("date")} data-testid="pf-date" /></div>
              <div><Label>Kesinti Ayı</Label><Input value={f.period} onChange={set("period")} placeholder="EKİM" /></div>
            </div>
            {!payment && (
              <div className="max-w-xs"><Label>Hazırlayan</Label>
                <Select value={f.prepared_by || ""} onValueChange={v => setF({ ...f, prepared_by: v })}>
                  <SelectTrigger data-testid="pf-prepared-by"><SelectValue placeholder={preparers.length ? "Kişi seçin" : "Önce Onaylayıcılar'dan kişi ekleyin"} /></SelectTrigger>
                  <SelectContent>{preparers.map(a => <SelectItem key={a.id} value={a.id}>{a.name}{a.title && ` · ${a.title}`}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="rounded-lg border border-border overflow-x-auto">
              <table className="w-full text-sm" data-testid="pf-lines">
                <thead className="bg-muted/50 text-xs text-muted-foreground"><tr>
                  <th className="text-left p-2">İmalat</th><th className="p-2">Birim Fiyat</th><th className="p-2">Önceki Küm.</th><th className="p-2">Kümülatif Miktar</th><th className="p-2">Bu Hakediş</th><th className="p-2 text-right">Bu Hak. Tutarı</th></tr></thead>
                <tbody>{contract.items.map((i, idx) => {
                  const prev = init.prevQty[i.id] || 0, cum = num(f.qty[i.id]);
                  return (
                    <tr key={i.id} className="border-t border-border">
                      <td className="p-2">{i.name} {i.unit && <span className="text-xs text-muted-foreground">({i.unit})</span>}</td>
                      <td className="p-2 text-center font-mono-nums">{fmtNum(i.unit_price)}</td>
                      <td className="p-2 text-center font-mono-nums">{fmtNum(prev)}</td>
                      <td className="p-2"><Input type="number" className="h-8" value={f.qty[i.id]} disabled={!!payment?.fixed}
                        onChange={e => setF({ ...f, qty: { ...f.qty, [i.id]: e.target.value } })} data-testid={`pf-qty-${idx}`} /></td>
                      <td className="p-2 text-center font-mono-nums">{fmtNum(cum - prev)}</td>
                      <td className="p-2 text-right font-mono-nums">{fmtTL((cum - prev) * i.unit_price)}</td>
                    </tr>);
                })}</tbody>
              </table>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>İlave İşler (Kümülatif)</Label><Input type="number" value={f.extra_cum} onChange={set("extra_cum")} data-testid="pf-extra" /></div>
              <div><Label>Avans Kesintisi</Label><Input type="number" value={f.advance_override} onChange={set("advance_override")} placeholder="Otomatik" data-testid="pf-advance" /></div>
              <div><Label>Diğer Kesintiler (M)</Label><Input type="number" value={f.other_ded} onChange={set("other_ded")} /></div>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-end gap-3">
                <div className="flex-1"><Label>SGK Prim Kesintisi (L)</Label><Input type="number" value={f.sgk} onChange={set("sgk")} data-testid="pf-sgk" /></div>
                <div className="flex-1"><Label className="text-xs">Alt yüklenici tahakkuk beyanı (yardımcı)</Label><Input type="number" value={f.sgk_declared || ""} onChange={set("sgk_declared")} /></div>
                <Button variant="outline" size="sm" onClick={sgkHelper} data-testid="pf-sgk-helper"><Calculator className="w-4 h-4 mr-1" /> Hesapla</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Yardımcı: (Kümülatif hakediş × %{contract.rates.sgk_rate * 100} − beyan) × %{contract.rates.sgk_prim * 100}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Fatura / Dekont Edilecek Kesintiler (KDV dahil)</div>
                <Button size="sm" variant="outline" onClick={pullDeductions} data-testid="pf-pull-deductions"><Download className="w-4 h-4 mr-1" /> Kesintilerden Getir</Button>
              </div>
              <div className="grid grid-cols-4 gap-2">
                {P_FIELDS.map(([k, l]) => (
                  <div key={k}><Label className="text-xs">{l}</Label><Input type="number" value={f.p?.[k] ?? 0} onChange={e => setF({ ...f, p: { ...f.p, [k]: e.target.value } })} data-testid={`pf-${k}`} /></div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">P1 Katılım payı otomatik: Kümülatif × %{contract.rates.katilim * 100} × (1+KDV) − önceki katılım payları.</p>
            </div>
          </div>
          <div className="lg:sticky lg:top-0 self-start">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Canlı Hesap</div>
            {calc && <div className="mb-2"><Warnings items={calc.warnings} testid="pf-warnings" /></div>}
            {calc ? <PaymentReport calc={calc.calc} compact /> : <div className="text-sm text-muted-foreground">Hesaplanıyor...</div>}
            {calc && <div className="text-xs text-muted-foreground mt-2" data-testid="pf-adv-remaining">Kalan avans: {fmtTL(calc.advance_remaining)}</div>}
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>İptal</Button>
          <Button onClick={save} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-slate-950" data-testid="pf-save">{saving ? "Kaydediliyor..." : "Kaydet"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
